import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';
import { deleteFolder } from '../../services/api/folderService';
import { useToast } from '../../hooks/use-toast';

interface DeleteFolderDialogProps {
    folderId: string;
    folderTitle: string;
    subFolders: { _id: string; title: string }[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDeleted: () => void;
}

export const DeleteFolderDialog = ({
    folderId,
    folderTitle,
    subFolders,
    open,
    onOpenChange,
    onDeleted,
}: DeleteFolderDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [deleting, setDeleting] = useState(false);
    const shownTitles = subFolders.slice(0, 5).map((sub) => sub.title);
    const hiddenCount = subFolders.length - shownTitles.length;

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteFolder(folderId);
            toast({
                title: t('folders.delete.toasts.deleted'),
                description: t('folders.delete.toasts.deletedDescription', {
                    title: folderTitle,
                }),
            });
            onDeleted();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.delete.toasts.failed'),
                variant: 'destructive',
            });
            setDeleting(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t('folders.delete.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('folders.delete.description', {
                            title: folderTitle,
                        })}
                    </AlertDialogDescription>
                    {subFolders.length > 0 && (
                        <p className="text-sm font-medium text-foreground">
                            {t('folders.delete.subFolders', {
                                count: subFolders.length,
                                names:
                                    hiddenCount > 0
                                        ? `${shownTitles.join(', ')} ${t('folders.delete.andMore', { count: hiddenCount })}`
                                        : shownTitles.join(', '),
                            })}
                        </p>
                    )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={deleting}>
                        {t('folders.delete.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        disabled={deleting}
                        onClick={handleDelete}
                        className={buttonVariants({ variant: 'destructive' })}
                    >
                        {t('folders.delete.confirm')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
